import { BookOpen, CalendarCheck, Car, CardSim, CarTaxiFront, Share2, Volume2 } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { AppStoreBadges } from "../components/AppStoreBadges";
import { Container } from "../components/Container";
import { PhoneMockup } from "../components/PhoneMockup";
import { useLocale } from "../hooks/useLocale";

const EXPERIENCE_CHIPS = [
  { icon: Volume2, key: "chip_audio" },
  { icon: BookOpen, key: "chip_itineraries" },
  { icon: Share2, key: "chip_social" },
] as const;

const PLAN_CHIPS = [
  { icon: CalendarCheck, key: "chip_booking" },
  { icon: Car, key: "chip_rentals" },
  { icon: CardSim, key: "chip_esim" },
  { icon: CarTaxiFront, key: "chip_taxi" },
] as const;

export function Hero() {
  const { t } = useTranslation();
  const { pathFor } = useLocale();
  const reduce = useReducedMotion();

  const fadeUp = (delay = 0) =>
    reduce
      ? { initial: false }
      : {
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.6, ease: "easeOut" as const, delay },
        };

  return (
    <section
      id="top"
      className="relative isolate overflow-hidden pt-20 sm:pt-24 lg:min-h-[80vh] lg:pt-28"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 dark:hidden"
        style={{
          background:
            "radial-gradient(60% 60% at 70% 30%, rgba(252,232,239,0.95), rgba(255,255,255,0) 70%)," +
            "radial-gradient(50% 50% at 10% 80%, rgba(252,232,239,0.6), rgba(255,255,255,0) 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 hidden dark:block"
        style={{
          background:
            "radial-gradient(60% 60% at 70% 30%, rgba(23,0,0,0.9), rgba(0,0,0,0) 70%)," +
            "radial-gradient(50% 50% at 10% 80%, rgba(227,28,93,0.18), rgba(0,0,0,0) 70%)",
        }}
      />

      <Container>
        <div className="grid items-center gap-10 pb-12 lg:grid-cols-12 lg:gap-14 lg:pb-20">
          <div className="lg:col-span-7">
            <motion.h1
              {...fadeUp(0.05)}
              className="hero-headline text-text-heading"
            >
              {t("hero.headline_prefix")}{" "}
              <span className="text-brand-500">
                {t("hero.headline_highlight")}
              </span>
              {t("hero.headline_suffix")}
            </motion.h1>

            <motion.p
              {...fadeUp(0.12)}
              className="mt-6 max-w-xl text-lg text-text-muted sm:text-xl"
            >
              {t("hero.subheadline_prefix")}{" "}
              <strong className="font-semibold text-text-heading">
                {t("hero.subheadline_countries")}
              </strong>
              {t("hero.subheadline_suffix")}
            </motion.p>

            <motion.div {...fadeUp(0.17)} className="mt-7 flex flex-col gap-4">
              {/* Experience row */}
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-brand-500">
                  {t("hero.experience_label")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {EXPERIENCE_CHIPS.map(({ icon: Icon, key }) => (
                    <span
                      key={key}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border-soft bg-bg-subtle px-3 py-1.5 text-xs font-medium text-text-body dark:bg-bg-muted"
                    >
                      <Icon className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
                      {t(`hero.${key}`)}
                    </span>
                  ))}
                </div>
              </div>

              {/* Plan row */}
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-brand-500">
                  {t("hero.plan_label")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {PLAN_CHIPS.map(({ icon: Icon, key }) => (
                    <span
                      key={key}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border-soft bg-bg-subtle px-3 py-1.5 text-xs font-medium text-text-body dark:bg-bg-muted"
                    >
                      <Icon className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
                      {t(`hero.${key}`)}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* App Store Download Badges */}
            <motion.div
              {...fadeUp(0.2)}
              id="download"
              className="mt-9"
            >
              <AppStoreBadges />

              <Link
                to={pathFor("/esim/shop")}
                className="mt-4 flex w-fit max-w-xl items-start gap-2.5 rounded-2xl border-2 border-brand-300 bg-brand-50 px-4 py-3 text-sm text-text-body shadow-sm transition-[border-color,background-color,box-shadow,transform] duration-150 hover:border-brand-500 hover:bg-brand-100 hover:shadow-md active:scale-[0.99] dark:border-brand-500/45 dark:bg-brand-500/15 dark:hover:border-brand-400 dark:hover:bg-brand-500/25"
              >
                <CardSim className="mt-0.5 h-5 w-5 flex-none text-brand-500" aria-hidden />
                <span className="font-semibold leading-snug text-text-heading">
                  {t("hero.esim_promo")}
                  <span className="mt-1 block text-sm font-semibold text-brand-500">
                    {t("hero.esim_shop_cta")}
                  </span>
                </span>
              </Link>
            </motion.div>
          </div>

          <motion.div
            {...fadeUp(0.15)}
            className="relative flex items-center justify-center lg:col-span-5"
            dir="ltr"
          >
            <PhoneMockup />
          </motion.div>
        </div>
      </Container>
    </section>
  );
}
